import {
	AutotaskLiveInformationProvider,
	InterestedAutotaskParty
} from "client_legacy/Workspace/LiveInformation/Autotask/provider";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { TileContext } from "../TileContext";
import { DestinationWindow } from "client/Api/Apps/common";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { BusyIndicator, DateString } from "client/Components";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { TicketListItem } from "client/Api/Workspace/LiveInformation/Autotask/autotaskLiveInformation";
import { ListItem } from "../ListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { AutotaskConnectionIdentifierViewModel } from "client/Api/Apps/EditApp/saveApp";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type AutotaskLiveTileContentProps = {
	provider: AutotaskLiveInformationProvider;
	onNavigate(): void;
	destinationWindow: DestinationWindow;
	connectionIdentifier: AutotaskConnectionIdentifierViewModel;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const AutotaskLiveTileContent = (props: AutotaskLiveTileContentProps): ReactElement => {
	const {
		provider,
		destinationWindow,
		connectionIdentifier,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [newTicketUrl, setNewTicketUrl] = useState<string | null>(null);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const timezone: TimeZone = useUserTimezone();

	const {
		icon,
		id: tileId,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const navigateToHandler = useCallback(
		(url: string): void => {
			addAnalyticsEvent();
			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			addAnalyticsEvent,
			destinationWindow
		]
	);

	const tileRefreshHandler = useCallback(
		(): void => {
			provider.forceReload();
		},
		[provider]
	);

	const titleSuffix = useMemo(
		(): string | undefined => {
			if (loadingState.isLoading
				|| loadingState.hasGlobalFailure
				|| loadingState.hasTooManyConsecutiveErrors
				|| loadingState.isNotConfigured
			) {
				return undefined;
			}

			const { items, hasMore } = loadingState.response;

			let amountText: string = items.length.toString();
			if (hasMore) {
				amountText += "+";
			}

			return `(${amountText})`;
		},
		[loadingState]
	);

	useEffect(
		(): (() => void) => {
			setLoadingState({ isLoading: true });
			setNewTicketUrl(null);

			const party: InterestedAutotaskParty = {
				partyId: tileId,
				resourceId: connectionIdentifier,
				informAboutAutotaskNotBeingConfigured: () => {
					setLoadingState({
						isLoading: false,
						isNotConfigured: true
					});
				},
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateCreateNewTicketUrl: setNewTicketUrl,
				updateItems: (response: LazyLoadPageResult<TicketListItem>) => {
					setLoadingState({
						isLoading: false,
						response
					});
				}
			};

			provider.register(party, { items: true });

			return (): void => provider.unregister(party);
		},
		[
			provider,
			connectionIdentifier,
			tileId
		]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[
			addAnalyticsEvent,
			onNavigate
		]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			new: () => navigateToHandler(
				ensureNotNil(newTicketUrl)
			)
		}),
		[navigateToHandler, newTicketUrl]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			newTicketUrl
		}),
		[newTicketUrl]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		const {
			hasGlobalFailure,
			hasTooManyConsecutiveErrors,
			isNotConfigured
		} = loadingState;

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasGlobalFailure) {
			return <LiveTileContent.Message text={localize("_ErrorDuringTicketsRetrieval")} />;
		}

		if (isNotConfigured) {
			return <LiveTileContent.Message text={localize("_IncompleteConfigurationPleaseContactYourAdmin")} />;
		}

		const {
			items
		} = loadingState.response;

		if (items.length === 0) {
			return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{items.map((x): ReactElement => (
						<ListItem
							key={x.id}
							trackingId={generateTrackingId("ticket", `|${x.id}`)}
							isHoverable
							onClick={() => navigateToHandler(x.portalUrl)}
							title={x.title}
						>
							<ListItem.Content>
								<ListItem.Row>
									<ListItem.Title>
										{x.title}
									</ListItem.Title>
								</ListItem.Row>

								<ListItem.Row>
									<ListItem.Info>
										{x.status}
									</ListItem.Info>
									<ListItem.RightAddition>
										<ListItem.Info>
											<DateString
												value={x.creationDate}
												format="AbsoluteTimestamp"
												timezone={timezone}
											/>
										</ListItem.Info>
									</ListItem.RightAddition>
								</ListItem.Row>
							</ListItem.Content>
						</ListItem>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header
				icon={icon}
				actions={defaultActions}
				actionContext={actionContext}
			>
				<LiveTileContent.Title
					text={localize("_MyOpenTickets")}
					suffix={titleSuffix}
				/>
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};

AutotaskLiveTileContent.displayName = "AutotaskLiveTileContent";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
	{
		hasTooManyConsecutiveErrors: true;
		hasGlobalFailure?: undefined;
		isNotConfigured?: undefined;
	}
	|
	{
		hasGlobalFailure: true;
		hasTooManyConsecutiveErrors?: undefined;
		isNotConfigured?: undefined;
	}
	|
	{
		isNotConfigured: true;
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
	}
	|
	{
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
		isNotConfigured?: undefined;
		response: LazyLoadPageResult<TicketListItem>;
	}
);

type ActionContext = {
	newTicketUrl: string | null;
};

type ActionHandlers = {
	new: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-ticket",
			title: localize("_NewTicket"),
			icon: "plus",
			canExecute: (x) => Boolean(x.newTicketUrl),
			execute: () => handlers.new()
		}
	];
}
